<template>
  <div class="ras-schedule">
    <!-- Why the schedules are locked (or that the chosen one is fine) -->
    <div v-if="blockers.length" class="riw-callout-stack ras-schedule__top">
      <div
        class="riw-callout"
        :class="selectedInvalid ? 'riw-callout--err' : 'riw-callout--warn'"
        :role="selectedInvalid ? 'alert' : 'status'">
        <v-icon class="riw-callout__icon">mdi-calendar-lock</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">{{ selectedInvalid ? `${selectedLabel} can’t be saved for this source.` : 'Only Manual Only is available for this source.' }}</span>
          <ul class="ras-list">
            <li v-for="blocker in blockers" :key="blocker.key">
              <b>{{ blocker.title }}</b> {{ blocker.text }}
              <button
                v-if="blocker.goStep"
                type="button"
                class="ras-link"
                @click="$emit('go-step', blocker.goStep)">{{ blocker.goLabel }}</button>
            </li>
          </ul>
        </div>
        <div v-if="selectedInvalid" class="riw-callout__actions">
          <v-btn small outlined class="riw-callout__btn" @click="setFrequency(FREQUENCY.MANUAL)">Switch to Manual Only</v-btn>
        </div>
      </div>
    </div>
    <div v-else-if="scheduled && identityLabel" class="riw-callout-stack ras-schedule__top">
      <div class="riw-callout riw-callout--ok riw-callout--compact">
        <v-icon class="riw-callout__icon">mdi-fingerprint</v-icon>
        <div class="riw-callout__body">
          <span class="riw-callout__title">Identity: {{ identityLabel }}.</span>Each {{ selectedLabel.toLowerCase() }} pull updates rows with the same “{{ identityColumn }}” and adds only new ones.
        </div>
      </div>
    </div>

    <div class="riw-section-label"><span class="riw-label-text">Pull frequency</span></div>
    <fieldset class="ras-radios">
      <legend class="riw-sr-only">Pull frequency</legend>
      <template v-for="option in options">
        <label
          :key="`o-${option.value}`"
          class="ras-radio"
          :class="{ 'is-disabled': option.disabled, 'is-invalid': option.invalid, 'is-on': option.selected }">
          <input
            type="radio"
            :name="`${_uid}-frequency`"
            :value="option.value"
            :checked="option.selected"
            :disabled="option.disabled"
            :aria-describedby="option.why ? `${_uid}-why-${option.value}` : null"
            @change="setFrequency(option.value)" />
          <v-icon size="18" class="ras-radio__icon">{{ option.icon }}</v-icon>
          <span class="ras-radio__main">
            <span class="ras-radio__label">{{ option.text }}</span>
            <span v-if="option.why" :id="`${_uid}-why-${option.value}`" class="ras-radio__why">
              <v-icon size="13">{{ option.invalid ? 'mdi-alert-circle-outline' : 'mdi-lock-outline' }}</v-icon>{{ option.why }}
            </span>
          </span>
        </label>

        <!-- Time pickers under the selected frequency -->
        <div v-if="option.selected && option.value === FREQUENCY.HOURLY" :key="`s-${option.value}`" class="ras-subsel">
          <v-select :value="value.minute" :items="minuteOptions" label="At minute" class="riw-input" dense outlined hide-details @change="patch({ minute: $event })" />
        </div>
        <div v-if="option.selected && option.value === FREQUENCY.DAILY" :key="`s-${option.value}`" class="ras-subsel">
          <v-select :value="value.hour" :items="hourOptions" label="Hour" class="riw-input" dense outlined hide-details @change="patch({ hour: $event })" />
          <v-select :value="value.minute" :items="minuteOptions" label="Minute" class="riw-input" dense outlined hide-details @change="patch({ minute: $event })" />
        </div>
        <div v-if="option.selected && option.value === FREQUENCY.WEEKLY" :key="`s-${option.value}`" class="ras-subsel">
          <v-select :value="value.dayOfWeek" :items="DayOfWeekOptions" label="Day" class="riw-input ras-subsel__wide" dense outlined hide-details @change="patch({ dayOfWeek: $event })" />
          <v-select :value="value.hour" :items="hourOptions" label="Hour" class="riw-input" dense outlined hide-details @change="patch({ hour: $event })" />
          <v-select :value="value.minute" :items="minuteOptions" label="Minute" class="riw-input" dense outlined hide-details @change="patch({ minute: $event })" />
        </div>
        <div v-if="option.selected && option.value === FREQUENCY.CUSTOM" :key="`s-${option.value}`" class="ras-subsel">
          <v-text-field
            :value="value.cronExpression"
            label="Cron expression"
            placeholder="0 */6 * * *"
            class="riw-input ras-subsel__cron"
            hint="min hour day month weekday — e.g. 0 8 * * 1-5 = weekdays at 08:00"
            persistent-hint
            dense
            outlined
            @input="patch({ cronExpression: $event })" />
        </div>
      </template>
    </fieldset>

    <div class="ras-divider"></div>

    <label class="ras-check" :class="{ 'is-disabled': registerUnset }">
      <input
        type="checkbox"
        :checked="!!value.triggerImmediate && !registerUnset"
        :disabled="registerUnset"
        :aria-describedby="registerUnset ? `${_uid}-cb-why` : null"
        @change="patch({ triggerImmediate: $event.target.checked })" />
      Run first import immediately after saving
    </label>
    <div v-if="registerUnset" :id="`${_uid}-cb-why`" class="ras-check__why">Unavailable until an administrator sets the register mode.</div>

    <div v-if="summary" class="ras-schedule__summary">
      <div class="riw-callout riw-callout--info riw-callout--compact">
        <v-icon class="riw-callout__icon">mdi-clock-outline</v-icon>
        <div class="riw-callout__body">{{ summary }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import { DayOfWeekOptions, ScheduleFrequencyOptions } from '@/features/Share/config/apiDataSourceEnums';
  import { FREQUENCY, frequencyLabel, isScheduled } from '../../../modules/apiSourceRules';
  import { scheduleSummary } from './apiSourcePresentation';

  const MINUTES = [0, 5, 10, 15, 20, 30, 45];

  /**
   * Schedule step of the Register API data source wizard. Non-Manual options are locked, each with
   * the reason, while `blockers` (apiSourceRules.scheduleBlockers) is non-empty; an already selected
   * schedule that breaks a rule is flagged with a one-click switch to Manual Only.
   */
  export default {
    name: 'ApiScheduleStep',
    props: {
      /** ScheduleConfigDto (v-model) */
      value: { type: Object, required: true },
      /** apiSourceRules.scheduleBlockers(...) */
      blockers: { type: Array, default: () => [] },
      /** Populated Facility Group register without a mode: no immediate first run. */
      registerUnset: { type: Boolean, default: false },
      identityLabel: { type: String, default: '' },
      identityColumn: { type: String, default: '' },
    },
    data() {
      return { FREQUENCY, DayOfWeekOptions };
    },
    computed: {
      scheduled() {
        return isScheduled(this.value.frequency);
      },
      selectedLabel() {
        return frequencyLabel(this.value.frequency);
      },
      /** The chosen schedule breaks a rule (only possible for an existing source or a changed rule). */
      selectedInvalid() {
        return this.blockers.length > 0 && this.scheduled;
      },
      options() {
        const locked = this.blockers.length > 0;
        const why = this.blockers.map((b) => b.short).join(' · ');
        return ScheduleFrequencyOptions.map((option) => {
          const selected = Number(this.value.frequency || 0) === option.value;
          const manual = option.value === FREQUENCY.MANUAL;
          return {
            ...option,
            selected,
            disabled: locked && !manual && !selected,
            invalid: locked && !manual && selected,
            why: locked && !manual ? why : '',
          };
        });
      },
      hourOptions() {
        return Array.from({ length: 24 }, (_, i) => ({ text: `${String(i).padStart(2, '0')}:00`, value: i }));
      },
      minuteOptions() {
        return MINUTES.map((m) => ({ text: `:${String(m).padStart(2, '0')}`, value: m }));
      },
      summary() {
        return scheduleSummary(this.value);
      },
    },
    methods: {
      patch(changes) {
        this.$emit('input', { ...this.value, ...changes });
      },
      setFrequency(frequency) {
        this.patch({ frequency });
      },
    },
  };
</script>
